import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Container,
  Divider,
  ListItemIcon,
  ListItemText,
  Select,
  FormControl
} from '@mui/material';
import {
  Logout as LogoutIcon,
  AccountCircle as AccountCircleIcon,
  Receipt as ReceiptIcon,
  Support as SupportIcon,
  Dashboard as DashboardIcon,
  SmartToy as SmartToyIcon,
  HelpOutline as HelpOutlineIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';
import { useCustomerPortal } from '../../contexts/CustomerPortalContext';

const CustomerNavbarV2: React.FC = () => {
  const { user, isAuthenticated, logout } = useCustomerAuth();
  // Cuenta gestionada por un partner → NADA de billing/pagos/planes Paddle en
  // el menú: la facturación la lleva el partner (la página además redirige).
  const { managedByReseller } = useCustomerPortal();
  const isManaged = !!managedByReseller;
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const homePath = isManaged ? '/customer/account' : '/customer/plans';

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/customer/login');
    handleClose();
  };

  const handleLanguageChange = (event: any) => {
    const newLang = event.target.value;
    i18n.changeLanguage(newLang);
    localStorage.setItem('language', newLang);
  };

  // «Ayuda» va SIEMPRE, y en las dos variantes del menú: es la guía que explica
  // el producto entero (empleados, usuarios, créditos, almacenamiento) y una
  // cuenta gestionada por un partner la necesita igual — más, si acaso, porque
  // parte de lo que lee no lo gobierna ella.
  const menuItems = isManaged
    ? [
        { path: '/customer/account', label: t('customer.account.title', 'Mi cuenta') },
        // Suscripción SÍ en cuentas gestionadas: es su PLAN DE CRÉDITOS (se lo
        // asigna su proveedor), no el billing Paddle — puede verlo aunque no lo
        // contrate aquí. Facturación (Paddle) sigue fuera de una cuenta gestionada.
        { path: '/customer/subscription', label: t('customer.subscription.title', 'Suscripción') },
        { path: '/customer/help', label: t('customer.help.title', 'Ayuda') },
        { path: '/customer/support', label: t('customer.support.title') }
      ]
    : [
        // /customer/plans ya no son «planes» de suscripción de tenants (tabla
        // `plans`, Paddle) sino el catálogo de PAQUETES DE CRÉDITOS del
        // producto de empleados IA: la etiqueta tiene que decir eso.
        { path: '/customer/plans', label: t('navigation.credits', 'Créditos') },
        { path: '/customer/account', label: t('customer.account.title', 'Mi cuenta') },
        { path: '/customer/subscription', label: t('customer.subscription.title') },
        { path: '/customer/billing', label: t('navigation.billing') },
        { path: '/customer/help', label: t('customer.help.title', 'Ayuda') },
        { path: '/customer/support', label: t('customer.support.title') }
      ];

  return (
    <AppBar 
      position="sticky" 
      elevation={0}
      sx={{
        bgcolor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
        color: 'text.primary'
      }}
    >
      <Container maxWidth="lg">
        <Toolbar disableGutters sx={{ minHeight: { xs: 64, md: 70 } }}>
          {/* Logo */}
          <Box
            onClick={() => navigate(isAuthenticated ? homePath : '/customer')}
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              cursor: 'pointer',
              mr: 4
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: 'primary.main',
                fontSize: '1.5rem'
              }}
            >
              WAI
            </Typography>
          </Box>

          {/* Navigation Links */}
          {isAuthenticated && (
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1, flexGrow: 1 }}>
              {menuItems.map((item) => (
                <Button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  sx={{
                    color: 'text.primary',
                    textTransform: 'none',
                    fontSize: '0.95rem',
                    fontWeight: 500,
                    '&:hover': {
                      bgcolor: 'action.hover'
                    }
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>
          )}

          {/* Right Side */}
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Language Selector */}
            <FormControl size="small" sx={{ minWidth: 90 }}>
              <Select
                value={i18n.language}
                onChange={handleLanguageChange}
                sx={{
                  '& .MuiSelect-select': {
                    py: 0.5,
                    fontSize: '0.875rem'
                  },
                  '& fieldset': {
                    borderColor: isAuthenticated ? 'rgba(0, 0, 0, 0.23)' : 'rgba(255, 255, 255, 0.23)'
                  }
                }}
              >
                <MenuItem value="es">🇪🇸 ES</MenuItem>
                <MenuItem value="en">🇬🇧 EN</MenuItem>
              </Select>
            </FormControl>

            {isAuthenticated ? (
              <>
                <IconButton
                  onClick={handleMenu}
                  size="small"
                >
                  <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main' }}>
                    {user?.full_name?.charAt(0).toUpperCase()}
                  </Avatar>
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleClose}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                  PaperProps={{
                    sx: {
                      mt: 1.5,
                      minWidth: 200,
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                    }
                  }}
                >
                  <Box sx={{ px: 2, py: 1.5 }}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {user?.full_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {user?.email}
                    </Typography>
                  </Box>
                  <Divider />
                  <MenuItem onClick={() => { navigate('/customer/account'); handleClose(); }}>
                    <ListItemIcon><SmartToyIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>{t('customer.account.title', 'Mi cuenta')}</ListItemText>
                  </MenuItem>
                  {/* Historial de créditos: la tabla larga del ledger vive en su
                      propia página. Va también en cuentas gestionadas (la
                      sección de créditos existe igual). */}
                  <MenuItem onClick={() => { navigate('/customer/creditos'); handleClose(); }}>
                    <ListItemIcon><HistoryIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>{t('customer.creditsHistory.title', 'Historial de créditos')}</ListItemText>
                  </MenuItem>
                  {/* Suscripción SIEMPRE (es el plan de créditos, existe también
                      en cuentas gestionadas); facturación Paddle solo en las NO
                      gestionadas. */}
                  <MenuItem onClick={() => { navigate('/customer/subscription'); handleClose(); }}>
                    <ListItemIcon><DashboardIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>{t('customer.subscription.title', 'Suscripción')}</ListItemText>
                  </MenuItem>
                  {!isManaged && (
                    <MenuItem onClick={() => { navigate('/customer/billing'); handleClose(); }}>
                      <ListItemIcon><ReceiptIcon fontSize="small" /></ListItemIcon>
                      <ListItemText>{t('navigation.billing')}</ListItemText>
                    </MenuItem>
                  )}
                  <MenuItem onClick={() => { navigate('/customer/profile'); handleClose(); }}>
                    <ListItemIcon><AccountCircleIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>{t('customer.profile.title')}</ListItemText>
                  </MenuItem>
                  {/* La guía también aquí: en móvil la barra de navegación se
                      oculta y este menú es el único camino a «Ayuda». */}
                  <MenuItem onClick={() => { navigate('/customer/help'); handleClose(); }}>
                    <ListItemIcon><HelpOutlineIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>{t('customer.help.title', 'Ayuda')}</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={() => { navigate('/customer/support'); handleClose(); }}>
                    <ListItemIcon><SupportIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>{t('customer.support.title')}</ListItemText>
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={handleLogout}>
                    <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>{t('auth.logout')}</ListItemText>
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <>
                <Button 
                  onClick={() => navigate('/customer/login')}
                  sx={{ textTransform: 'none', fontWeight: 500 }}
                >
                  {t('auth.login')}
                </Button>
                <Button 
                  variant="contained" 
                  onClick={() => navigate('/customer/register')}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 2,
                    boxShadow: 'none'
                  }}
                >
                  {t('customer.auth.signUp')}
                </Button>
              </>
            )}
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default CustomerNavbarV2;

